import React, { useState, useMemo, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { appointmentService } from '../../../services/supabaseService';
import InterventionModal from './interventionModal'; 


const getStatusLabel = (status) => {
  const statusMap = {
    'scheduled': 'Planifié',
    'on_site': 'Sur place',
    'in_progress': 'En cours',
    'completed': 'Terminé',
    'cancelled': 'Annulé',
    'pending': 'En attente',
    'planned': 'Planifié'
  };
  return statusMap[status] || status;
};


const getStatusBadge = (status) => {
  const styles = {
    'scheduled': 'bg-blue-100 text-blue-700 border-blue-200',
    'planned': 'bg-blue-100 text-blue-700 border-blue-200',
    'on_site': 'bg-green-100 text-green-700 border-green-200',
    'in_progress': 'bg-yellow-100 text-yellow-700 border-yellow-200',
    'completed': 'bg-purple-100 text-purple-700 border-purple-200',
    'cancelled': 'bg-red-100 text-red-700 border-red-200',
    'pending': 'bg-orange-100 text-orange-700 border-orange-200'
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${styles[status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
      {getStatusLabel(status)}
    </span>
  );
};


const getUrgencyBadge = (urgency) => {
  const styles = {
    'today': 'bg-red-100 text-red-700 border-red-200',
    'urgent': 'bg-orange-100 text-orange-700 border-orange-200',
    'standard': 'bg-blue-100 text-blue-700 border-blue-200',
    'high': 'bg-red-100 text-red-700 border-red-200',
    'medium': 'bg-orange-100 text-orange-700 border-orange-200',
    'low': 'bg-green-100 text-green-700 border-green-200'
  };

  const labels = {
    'today': 'Aujourd\'hui',
    'within-hour': 'Sous 2h',
    'urgent': 'Urgent',
    'standard': 'Standard',
    'high': 'Haute',
    'medium': 'Moyenne',
    'low': 'Basse'
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${styles[urgency] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
      {labels[urgency] || urgency}
    </span>
  );
};

const InterventionGrid = ({
  selectedItems = [],
  onSelectionChange,
  searchTerm,
  onSearchChange,
  currentUserRole,
  currentUserId,
  onOpenQuoteModal,
  quoteListVersion = 0,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'scheduled_date', direction: 'desc' });
  const [filterConfig, setFilterConfig] = useState({
    status: 'all',
    urgency: 'all'
  });
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  
  
  const [isInterventionModalOpen, setIsInterventionModalOpen] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  
  
  const menuRefs = useRef({});
  
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  
  useEffect(() => {
    const handleClickOutside = (event) => {
      
      if (menuOpenId) {
        const currentMenuRef = menuRefs.current[menuOpenId];
        
        if (currentMenuRef && !currentMenuRef.contains(event.target)) {
          setMenuOpenId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpenId]);

  
  useEffect(() => {
    const fetchInterventions = async () => {
      try {
        setLoading(true);
        
        
        const { data: appointments, error: appointmentsError } = await appointmentService.getAppointments();
        
        if (appointmentsError) {
          throw appointmentsError;
        }

        console.log('Données récupérées:', appointments);

        
        const transformedData = appointments?.map(appointment => ({
          id: appointment.id,
          is_quote: appointment.is_quote || false,
          reference: appointment.reference || `INT-${appointment.id?.substring(0, 8)}`,
          technician: appointment.technician?.full_name || appointment.technician_name || 'Non assigné',
          technician_id: appointment.technician_id,
          customer_name: appointment.customer_name || 'Client',
          customer_email: appointment.customer_email,
          customer_phone: appointment.customer_phone,
          client: appointment.client || null,
          siret: appointment.siret || null,
          service_type: appointment.service_type || appointment.Service?.name || 'Service',
          service_id: appointment.Service,
          problem_name: appointment.Service?.problem?.name || null,
          urgency: appointment.urgency || 'standard',
          status: appointment.status || 'scheduled',
          scheduled_date: appointment.scheduled_date,
          scheduled_time_start: appointment.scheduled_time_start,
          scheduled_time_end: appointment.scheduled_time_end,
          customer_address: appointment.customer_address,
          customer_latitude: appointment.customer_latitude,
          customer_longitude: appointment.customer_longitude,
          estimated_arrival_time: appointment.estimated_arrival_time,
          actual_arrival_time: appointment.actual_arrival_time,
          completion_time: appointment.completion_time,
          notes: appointment.notes,
          accessInfo: appointment.accessInfo || appointment.access_info || '',
          photosclient: appointment.photosclient,
          price_subtotal: appointment.price_subtotal || '0',
          price_vat: appointment.price_vat || '0',
          price_total: appointment.price_total || '0',
          payment_status: appointment.payment_status,
          payment_method: appointment.payment_method,
          created_at: appointment.created_at,
          appointment_result: appointment.appointment_result || null,
          tva: appointment.tva ?? null,
          latest_quote: (() => {
            const quotes = Array.isArray(appointment.appointment_quote)
              ? appointment.appointment_quote
              : appointment.appointment_quote
                ? [appointment.appointment_quote]
                : [];
            if (!quotes.length) return null;
            return [...quotes].sort(
              (a, b) =>
                new Date(b.created_at || b.updated_at || 0) -
                new Date(a.created_at || a.updated_at || 0)
            )[0];
          })(),

          originalData: appointment
        })) || [];

        setInterventions(transformedData);
        setTotalItems(transformedData.length);
      } catch (err) {
        console.error('Erreur lors du chargement des interventions:', err);
        setError('Erreur lors du chargement des interventions: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInterventions();
  }, [quoteListVersion]);

  
  const filteredInterventions = useMemo(() => {
    let filtered = interventions;

    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered?.filter((intervention) =>
        intervention?.reference?.toLowerCase()?.includes(term) ||
        intervention?.customer_name?.toLowerCase()?.includes(term) ||
        intervention?.service_type?.toLowerCase()?.includes(term) ||
        intervention?.technician?.toLowerCase()?.includes(term) ||
        intervention?.customer_email?.toLowerCase()?.includes(term) ||
        intervention?.customer_phone?.includes(term)
      );
    }

    
    if (filterConfig?.status !== 'all') {
      filtered = filtered?.filter((intervention) => intervention?.status === filterConfig?.status);
    }
    if (filterConfig?.urgency !== 'all') {
      filtered = filtered?.filter((intervention) => intervention?.urgency === filterConfig?.urgency);
    }

    return filtered;
  }, [interventions, searchTerm, filterConfig]);

  
  const sortedInterventions = useMemo(() => {
    const sorted = [...filteredInterventions]?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      
      if (sortConfig?.key === 'updated_at') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      
      if (sortConfig?.key === 'price_total') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredInterventions, sortConfig]);

  
  const paginatedInterventions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedInterventions.slice(startIndex, endIndex);
  }, [sortedInterventions, currentPage, itemsPerPage]);

  
  useEffect(() => {
    setTotalItems(sortedInterventions.length);
    
    setCurrentPage(1);
  }, [sortedInterventions.length, searchTerm, filterConfig, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(paginatedInterventions);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectIntervention = (intervention, checked) => {
    if (checked) {
      onSelectionChange([...selectedItems, intervention]);
    } else {
      onSelectionChange(selectedItems?.filter((item) => item?.id !== intervention?.id));
    }
  };

  const handleCreateQuote = (intervention) => {
    console.log('Établir un devis pour:', intervention);
    
    onOpenQuoteModal(intervention);
    
  };

  
  const handleViewDetails = (intervention) => {
    setSelectedIntervention(intervention);
    setIsInterventionModalOpen(true);
    setMenuOpenId(null); 
  };

  
  const handleCloseModal = () => {
    setIsInterventionModalOpen(false);
    setSelectedIntervention(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    if (timeStr.includes(':')) {
      return timeStr.substring(0, 5);
    }
    return timeStr;
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price) || 0;
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(numPrice);
  };

  
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const allSelected = paginatedInterventions?.length > 0 && selectedItems?.length === paginatedInterventions?.length;
  const someSelected = selectedItems?.length > 0 && selectedItems?.length < paginatedInterventions?.length;

  
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  
  if (loading && interventions.length === 0) {
    return (
      <div className="h-full bg-card flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Chargement des interventions...</p>
        </div>
      </div>
    );
  }

  
  if (error) {
    return (
      <div className="h-full bg-card flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-error mb-4" />
          <p className="text-error mb-2">{error}</p>
          <Button 
            variant="outline" 
            onClick={() => window.location.reload()}
          >
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full bg-card flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Interventions</h3>
              <p className="text-sm text-muted-foreground">
                {totalItems} intervention{totalItems > 1 ? 's' : ''} au total
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Rechercher par référence, client, service ou technicien..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e?.target?.value)}
                className="w-full"
                icon="Search"
              />
            </div>
            
            <select
              value={filterConfig?.status}
              onChange={(e) => setFilterConfig((prev) => ({ ...prev, status: e?.target?.value }))}
              className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">Tous les statuts</option>
              <option value="planned">Planifié</option>
              <option value="on_site">Sur place</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Terminé</option>
              <option value="cancelled">Annulé</option>
              <option value="pending">En attente</option>
            </select>

            <select
              value={filterConfig?.urgency}
              onChange={(e) => setFilterConfig((prev) => ({ ...prev, urgency: e?.target?.value }))}
              className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">Toutes les urgences</option>
              <option value="today">Aujourd'hui</option>
              <option value="urgent">Urgent</option>
              <option value="high">Haute</option>
              <option value="medium">Moyenne</option>
              <option value="standard">Standard</option>
              <option value="low">Basse</option>
            </select>
          </div>
        </div>

        {/* Table avec header sticky et fond plein */}
        <div className="flex-1 overflow-auto relative">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-20">
              <tr className="bg-gray-100 border-b-2 border-gray-200">
                <th className="w-12 p-3 text-left bg-gray-100">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => handleSelectAll(e?.target?.checked)}
                  />
                </th>
                <th className="p-3 text-left bg-gray-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('reference')}
                    className="h-auto p-0 font-medium text-foreground hover:text-primary bg-transparent"
                  >
                    Référence
                    <Icon
                      name={sortConfig?.key === 'reference' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                      size={14}
                      className="ml-1"
                    />
                  </Button>
                </th>
                <th className="p-3 text-left bg-gray-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('scheduled_date')}
                    className="h-auto p-0 font-medium text-foreground hover:text-primary bg-transparent"
                  >
                    Date & Heure
                    <Icon
                      name={sortConfig?.key === 'scheduled_date' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                      size={14}
                      className="ml-1"
                    />
                  </Button>
                </th>
                <th className="p-3 text-left bg-gray-100">Client</th>
                <th className="p-3 text-left bg-gray-100">Service</th>
                <th className="p-3 text-left bg-gray-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('technician')}
                    className="h-auto p-0 font-medium text-foreground hover:text-primary bg-transparent"
                  >
                    Technicien
                    <Icon
                      name={sortConfig?.key === 'technician' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                      size={14}
                      className="ml-1"
                    />
                  </Button>
                </th>
                <th className="p-3 text-left bg-gray-100">Adresse</th>
                <th className="p-3 text-left bg-gray-100">Statut</th>
                <th className="p-3 text-left bg-gray-100">Urgence</th>
                <th className="p-3 text-left bg-gray-100">statut de paiement</th>
                <th className="p-3 text-left bg-gray-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('price_total')}
                    className="h-auto p-0 font-medium text-foreground hover:text-primary bg-transparent"
                  >
                    Total TTC
                    <Icon
                      name={sortConfig?.key === 'price_total' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                      size={14}
                      className="ml-1"
                    />
                  </Button>
                </th>
                <th className="w-12 p-3 text-left bg-gray-100"></th>
              </tr>
            </thead>
            <tbody>
              {paginatedInterventions?.map((intervention) => {
                const isSelected = selectedItems?.some((item) => item?.id === intervention?.id);
                const isMenuOpen = menuOpenId === intervention?.id;

                return (
                  <tr key={intervention?.id} className={`border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-micro`}>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleSelectIntervention(intervention, e?.target?.checked)}
                      />
                    </td>
                    <td className="p-3">
                      <span className="text-sm font-mono font-medium text-gray-900">
                        {intervention?.reference}
                      </span>
                    </td>
                    <td className="p-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(intervention?.scheduled_date)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatTime(intervention?.scheduled_time_start)} - {formatTime(intervention?.scheduled_time_end)}
                        </p>
                      </div>
                    </td>
                    <td className="p-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{intervention?.customer_name}</p>
                        <p className="text-xs text-gray-500">{intervention?.customer_phone || intervention?.customer_email}</p>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-gray-900">{intervention?.service_type}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center">
                        <Icon name="User" size={14} className="text-gray-500 mr-1" />
                        <span className="text-sm text-gray-900">{intervention?.technician || 'Non assigné'}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="max-w-xs">
                        <p className="text-sm text-gray-500 truncate" title={intervention?.customer_address}>
                          {intervention?.customer_address || 'Adresse non spécifiée'}
                        </p>
                      </div>
                    </td>
                    <td className="p-3">
                      {getStatusBadge(intervention?.status)}
                    </td>
                    <td className="p-3">
                      {getUrgencyBadge(intervention?.urgency)}
                    </td>
                    <td className="p-3">
                      <span className={`text-sm font-medium ${ intervention?.is_quote ? 'text-blue-600' : intervention?.payment_status === 'succeeded' ? 'text-green-600' : 'text-red-600'}`}>
                        {intervention?.is_quote ? 'Sur Devis' : intervention?.payment_status === 'succeeded' ? 'Payé' : 'Non payé'}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm font-medium text-gray-900">
                        {intervention?.is_quote ? 'Sur Devis' : formatPrice(intervention?.price_total)}
                      </span>
                    </td>
                    <td className="p-3 relative">
                      <>
                        {/* SOLUTION: Chaque ligne a sa propre ref dans l'objet menuRefs */}
                        <div ref={el => menuRefs.current[intervention.id] = el}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e?.stopPropagation();
                              setMenuOpenId(isMenuOpen ? null : intervention?.id);
                            }}
                            className="bg-transparent hover:bg-gray-100"
                          >
                            <Icon name="MoreVertical" size={16} />
                          </Button>

                          {/* Menu déroulant */}
                          {isMenuOpen && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-30 border border-gray-200 py-1">
                              {/* detail intervention */}
                              <button
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  handleViewDetails(intervention);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Icon name="Eye" size={16} className="text-gray-600" />
                                <span>Voir détails intervention</span>
                              </button>
                              {intervention?.is_quote && (
                                <button
                                  onClick={(e) => {
                                    e?.stopPropagation();
                                    handleCreateQuote(intervention);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Icon
                                    name={
                                      intervention?.latest_quote?.status === 'accepted'
                                        ? 'CheckCircle'
                                        : intervention?.latest_quote?.status === 'rejected'
                                          ? 'XCircle'
                                          : 'FileText'
                                    }
                                    size={16}
                                    className={
                                      intervention?.latest_quote?.status === 'accepted'
                                        ? 'text-green-600'
                                        : intervention?.latest_quote?.status === 'rejected'
                                          ? 'text-red-600'
                                          : 'text-blue-600'
                                    }
                                  />
                                  <span>
                                    {!intervention?.latest_quote
                                      ? 'Établir un devis'
                                      : intervention.latest_quote.status === 'accepted'
                                        ? 'Réponse client : Oui'
                                        : intervention.latest_quote.status === 'rejected'
                                          ? 'Réponse client : Non'
                                          : 'Devis envoyé'}
                                  </span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {paginatedInterventions?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="Calendar" size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune intervention trouvée</h3>
              <p className="text-sm text-gray-500 text-center max-w-md">
                {searchTerm ? 'Essayez d\'ajuster vos termes de recherche ou vos filtres.' : 'Aucune intervention pour le moment.'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages >= 1 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-600 flex items-center gap-4">
              {totalItems > 0 ? (
                <>Affichage de {startItem} à {endItem} sur {totalItems} interventions</>
              ) : (
                <>Aucune intervention à afficher</>
              )}
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value={10}>10 par page</option>
                <option value={25}>25 par page</option>
                <option value={50}>50 par page</option>
                <option value={100}>100 par page</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              {/* Bouton Première page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className="px-2"
              >
                <Icon name="ChevronsLeft" size={16} />
              </Button>
              
              {/* Bouton Précédent */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2"
              >
                <Icon name="ChevronLeft" size={16} />
              </Button>

              {/* Numéros de page */}
              <div className="flex items-center space-x-1">
                {getPageNumbers().map((page, index) => (
                  <React.Fragment key={index}>
                    {page === '...' ? (
                      <span className="px-3 py-1 text-sm text-gray-500">...</span>
                    ) : (
                      <Button
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => goToPage(page)}
                        className={`min-w-[32px] ${currentPage === page ? 'bg-primary text-white' : ''}`}
                      >
                        {page}
                      </Button>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Bouton Suivant */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2"
              >
                <Icon name="ChevronRight" size={16} />
              </Button>

              {/* Bouton Dernière page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2"
              >
                <Icon name="ChevronsRight" size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Footer avec sélection */}
        {selectedItems?.length > 0 && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-900">
                {selectedItems?.length} intervention{selectedItems?.length > 1 ? 's' : ''} sélectionnée{selectedItems?.length > 1 ? 's' : ''}
              </span>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" iconName="UserCheck" iconPosition="left" iconSize={14}>
                  Assigner technicien
                </Button>
                <Button variant="outline" size="sm" iconName="Clock" iconPosition="left" iconSize={14}>
                  Modifier statut
                </Button>
                <Button variant="outline" size="sm" iconName="Download" iconPosition="left" iconSize={14}>
                  Exporter
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'intervention */}
      <InterventionModal
        isOpen={isInterventionModalOpen}
        onClose={handleCloseModal}
        intervention={selectedIntervention}
      />
    </>
  );
};

export default InterventionGrid;